import { AsyncPipe, NgIf } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { LocalStorageManager } from '@cutting/main/shared/helper/local-storage-manager.helper';
import { FacadesService } from '@cutting/main/shared/services/facades.service';
import {
  CuttingClientMaterial,
  CuttingFacadeMaterial,
  CuttingSettings,
  FacadeComponentAvailability,
  FacadeTextureDirectionEnum,
  FacadeView,
  Guid,
  NgxLocalizedNumbersService,
} from 'cloud-shared-lib';
import * as _ from 'lodash';
import { Observable, tap } from 'rxjs';

import { ClientMaterialService } from '@cutting/main/shared/services/client-material.service';
import { FacadesModule } from './facades.module';
import { PanelListComponent } from './panel-list/panel-list.component';
import { PanelSchemaComponent } from './panel-schema/panel-schema.component';

export class FacadesBase {}

// eslint-disable-next-line no-underscore-dangle
export const _FacadesMixinBase = mixinDisabled(FacadesBase);

@Component({
  selector: 'cutting-facades',
  styleUrls: ['./facades.component.scss'],
  templateUrl: './facades.component.html',
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
  standalone: true,
  imports: [
    NgIf,
    AsyncPipe,
    MatDividerModule,
    MatButtonModule,
    FacadesModule,
    PanelSchemaComponent,
    PanelListComponent,
  ],
})
export class FacadesComponent extends _FacadesMixinBase implements OnInit, CanDisable {
  @Input() orderId = 0;
  @Input() componentsAvailability?: FacadeComponentAvailability;
  @Input({ required: true }) cuttingSettings: CuttingSettings;

  editMode = false;
  editedPanel: FacadeView;

  panels: FacadeView[] = [];

  materials$: Observable<CuttingFacadeMaterial[]>;

  get userId(): number {
    return LocalStorageManager.regData;
  }

  get isExistFilmMaterialsWithoutFilm(): boolean {
    return this.editedPanel?.material?.film && _.isNil(this.editedPanel.panelOutline.filmMaterial);
  }

  constructor(
    private localizedNumbersService: NgxLocalizedNumbersService,
    private facadesService: FacadesService,
    private clientMaterialService: ClientMaterialService
  ) {
    super();

    this.resetEditedPanel();
  }

  ngOnInit(): void {
    this.materials$ = this.facadesService.getFacadeMaterials(this.orderId).pipe(
      tap((response: CuttingFacadeMaterial[]) => {
        response = response.filter(m => m.facades.length);

        this.panels = this.material2Facades(response);

        response.forEach(m => {
          this.clientMaterialService.updateMaterials('facade', {
            article: m.article,
            name: m.name,
            inMaterialBaseId: m.inMaterialBaseId,
            type: m.type,
            preparedForDeletion: false,
            cuttings: [],
            id: 0,
          } as CuttingClientMaterial);
        });
      })
    );
  }

  getCurrencySymbol(): string {
    return this.localizedNumbersService.getCurrentLocaleDefinition().currency;
  }

  addPanel(): void {
    const newPanel = _.cloneDeep(this.editedPanel);
    newPanel.uuid = Guid.newGuid();
    this.panels = [newPanel, ...this.panels];
  }

  savePanel(): void {
    const panelFromList = this.panels.find(p => p.uuid === this.editedPanel.uuid);

    if (panelFromList) {
      _.mergeWith(panelFromList, this.editedPanel, (a, b) => {
        if (_.isObject(b) || _.isArray(b)) {
          return _.cloneDeep(b);
        }
        return b;
      });
    } else {
      this.addPanel();
    }

    this.stopEditMode();
  }

  stopEditMode(): void {
    this.editMode = false;
  }

  edit(panel: FacadeView): void {
    this.editedPanel = _.cloneDeep(panel);
    this.editMode = true;
  }

  changeTextureDirection(panel: FacadeView): void {
    this.editedPanel = panel;
  }

  private resetEditedPanel(): void {
    this.editedPanel = new FacadeView({
      orientation: true,
      textureDirection: FacadeTextureDirectionEnum.AlongWidth,
      height: 0,
      width: 0,
      count: 1,
    });
  }

  private material2Facades(materials: CuttingFacadeMaterial[]): FacadeView[] {
    return _.flatMap(materials, m => m.facades.map(p => ({ ...p, material: m } as FacadeView)));
  }
}
